import { useEffect, useState } from 'react'
import { RefreshCw, Filter, ArrowUp, ArrowDown, Search, X } from 'lucide-react'
import { eventsAPI } from '../api/client'
import { useOrg } from '../contexts/OrgContext'

function EventsPage() {
  const { currentOrg } = useOrg()
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [sortColumn, setSortColumn] = useState(null)
  const [sortDirection, setSortDirection] = useState('asc')
  const [activeTab, setActiveTab] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  useEffect(() => {
    if (currentOrg?.org_id) {
      loadEvents()
    }
  }, [currentOrg?.org_id])

  async function loadEvents() {
    if (!currentOrg?.org_id) return

    try {
      setLoading(true)
      setError('')
      const response = await eventsAPI.getAll({ org_id: currentOrg.org_id, limit: 100 })
      setEvents(response.data?.events || [])
    } catch (err) {
      setError(err.message || 'Failed to load events')
      console.error('Events error:', err)
    } finally {
      setLoading(false)
    }
  }

  function formatTimestamp(timestamp) {
    return new Date(timestamp).toLocaleString()
  }

  function formatEventType(eventType) {
    const typeMap = {
      'card_scan': 'Card Scan',
      'access_granted': 'Access Granted',
      'access_denied': 'Access Denied',
      'door_access_granted': 'Door Access (Granted)',
      'door_access_denied': 'Door Access (Denied)',
      'roster_download': 'Roster Download',
      'tanning_session_start': 'Tanning Session Start',
      'tanning_session_stop': 'Tanning Session Stop',
      'verification_scan': 'Verification Scan',
      'card_assigned': 'Card Assigned',
      'card_unassigned': 'Card Unassigned',
      'client_created': 'Client Created',
      'client_updated': 'Client Updated',
      'client_deleted': 'Client Deleted',
      'portal_login': 'Portal Login',
      'portal_logout': 'Portal Logout'
    }
    return typeMap[eventType] || eventType
  }

  function getEventTypeBadge(eventType) {
    if (eventType === 'access_granted' || eventType?.includes('door_access_granted') ||
        eventType === 'card_assigned' || eventType === 'tanning_session_start' ||
        eventType === 'verification_scan') {
      return 'badge-success'
    } else if (eventType === 'access_denied' || eventType?.includes('door_access_denied') ||
               eventType === 'card_unassigned' || eventType === 'client_deleted') {
      return 'badge-danger'
    } else if (eventType?.includes('tanning_session_stop') || eventType === 'roster_download') {
      return 'badge-info'
    } else if (eventType?.includes('client_created') || eventType?.includes('client_updated')) {
      return 'badge-warning'
    }
    return 'badge-info'
  }

  // Filter events by tab
  function filterByTab(events, tab) {
    switch (tab) {
      case 'all':
        return events
      case 'device':
        return events.filter(e =>
          e.event_type === 'roster_download' ||
          e.event_type === 'verification_scan' ||
          e.event_type === 'card_scan' ||
          e.event_type === 'access_granted' ||
          e.event_type === 'access_denied'
        )
      case 'client_card':
        return events.filter(e =>
          e.event_type === 'card_assigned' ||
          e.event_type === 'card_unassigned' ||
          e.event_type === 'client_created' ||
          e.event_type === 'client_updated' ||
          e.event_type === 'client_deleted'
        )
      case 'access':
        return events.filter(e =>
          e.event_type === 'door_access_granted' ||
          e.event_type === 'door_access_denied'
        )
      case 'sessions':
        // Show only completed sessions (pairs of start/stop)
        return events.filter(e =>
          e.event_type === 'tanning_session_start' ||
          e.event_type === 'tanning_session_stop'
        )
      default:
        return events
    }
  }

  // Get filtered and sorted events
  function getFilteredEvents() {
    let filtered = filterByTab(events, activeTab)

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(e => e.status === statusFilter)
    }

    // Apply search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      filtered = filtered.filter(e =>
        e.device_name?.toLowerCase().includes(searchLower) ||
        e.client_name?.toLowerCase().includes(searchLower) ||
        e.card_id?.toLowerCase().includes(searchLower) ||
        e.user_email?.toLowerCase().includes(searchLower) ||
        e.event_type?.toLowerCase().includes(searchLower)
      )
    }

    return filtered
  }

  const filteredEvents = getFilteredEvents()

  // Get tab counts
  const tabCounts = {
    all: events.length,
    device: filterByTab(events, 'device').length,
    client_card: filterByTab(events, 'client_card').length,
    access: filterByTab(events, 'access').length,
    sessions: filterByTab(events, 'sessions').length
  }

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  // Sort events if sort column is set
  const sortedEvents = sortColumn ? [...filteredEvents].sort((a, b) => {
    let aVal, bVal

    switch (sortColumn) {
      case 'timestamp':
        aVal = new Date(a.timestamp).getTime()
        bVal = new Date(b.timestamp).getTime()
        break
      case 'device':
        aVal = a.device_name || a.device_id || ''
        bVal = b.device_name || b.device_id || ''
        break
      case 'card_id':
        aVal = a.card_id || ''
        bVal = b.card_id || ''
        break
      case 'client':
        aVal = a.client_name || ''
        bVal = b.client_name || ''
        break
      case 'status':
        aVal = a.status || ''
        bVal = b.status || ''
        break
      case 'event_type':
        aVal = a.event_type || ''
        bVal = b.event_type || ''
        break
      default:
        return 0
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
    return 0
  }) : filteredEvents

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    )
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Event Log <span style={{ fontSize: '1.25rem', fontWeight: '500', color: 'var(--text-light)' }}>({events.length})</span></h1>
          <p>System-wide event tracking and audit trail</p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button className="btn btn-secondary" onClick={loadEvents}>
            <RefreshCw size={18} />
            Refresh
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Tabs */}
      <div style={{ marginBottom: '1.5rem', borderBottom: '2px solid var(--border)' }}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={() => setActiveTab('all')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'all' ? 'var(--primary)' : 'transparent',
              color: activeTab === 'all' ? '#fff' : 'var(--text)',
              border: 'none',
              borderBottom: activeTab === 'all' ? '3px solid var(--primary)' : '3px solid transparent',
              cursor: 'pointer',
              fontWeight: activeTab === 'all' ? '600' : '500',
              fontSize: '0.875rem',
              transition: 'all 0.2s'
            }}
          >
            All ({tabCounts.all})
          </button>
          <button
            onClick={() => setActiveTab('device')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'device' ? 'var(--primary)' : 'transparent',
              color: activeTab === 'device' ? '#fff' : 'var(--text)',
              border: 'none',
              borderBottom: activeTab === 'device' ? '3px solid var(--primary)' : '3px solid transparent',
              cursor: 'pointer',
              fontWeight: activeTab === 'device' ? '600' : '500',
              fontSize: '0.875rem',
              transition: 'all 0.2s'
            }}
          >
            Device ({tabCounts.device})
          </button>
          <button
            onClick={() => setActiveTab('client_card')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'client_card' ? 'var(--primary)' : 'transparent',
              color: activeTab === 'client_card' ? '#fff' : 'var(--text)',
              border: 'none',
              borderBottom: activeTab === 'client_card' ? '3px solid var(--primary)' : '3px solid transparent',
              cursor: 'pointer',
              fontWeight: activeTab === 'client_card' ? '600' : '500',
              fontSize: '0.875rem',
              transition: 'all 0.2s'
            }}
          >
            Client/Card ({tabCounts.client_card})
          </button>
          <button
            onClick={() => setActiveTab('access')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'access' ? 'var(--primary)' : 'transparent',
              color: activeTab === 'access' ? '#fff' : 'var(--text)',
              border: 'none',
              borderBottom: activeTab === 'access' ? '3px solid var(--primary)' : '3px solid transparent',
              cursor: 'pointer',
              fontWeight: activeTab === 'access' ? '600' : '500',
              fontSize: '0.875rem',
              transition: 'all 0.2s'
            }}
          >
            Access ({tabCounts.access})
          </button>
          <button
            onClick={() => setActiveTab('sessions')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'sessions' ? 'var(--primary)' : 'transparent',
              color: activeTab === 'sessions' ? '#fff' : 'var(--text)',
              border: 'none',
              borderBottom: activeTab === 'sessions' ? '3px solid var(--primary)' : '3px solid transparent',
              cursor: 'pointer',
              fontWeight: activeTab === 'sessions' ? '600' : '500',
              fontSize: '0.875rem',
              transition: 'all 0.2s'
            }}
          >
            Sessions ({tabCounts.sessions})
          </button>
        </div>
      </div>

      {/* Search and Filter Controls */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Search Bar */}
        <div style={{ flex: '1', minWidth: '250px', position: 'relative' }}>
          <Search size={18} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-light)' }} />
          <input
            type="text"
            placeholder="Search events..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ paddingLeft: '2.5rem', paddingRight: searchTerm ? '2.5rem' : '0.75rem' }}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              style={{
                position: 'absolute',
                right: '0.5rem',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--text-light)',
                padding: '0.25rem',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Status Filter */}
        <div style={{ minWidth: '150px' }}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ width: '100%' }}
          >
            <option value="all">All Status</option>
            <option value="granted">Granted</option>
            <option value="denied">Denied</option>
            <option value="success">Success</option>
            <option value="started">Started</option>
            <option value="stopped">Stopped</option>
            <option value="verified">Verified</option>
          </select>
        </div>

        {/* Results Count */}
        <div style={{ color: 'var(--text-light)', fontSize: '0.875rem', fontWeight: '500' }}>
          Showing {sortedEvents.length} of {events.length} events
        </div>
      </div>

      <div className="card">
        {sortedEvents.length === 0 ? (
          <p className="no-data">No events found</p>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th onClick={() => handleSort('timestamp')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Timestamp
                      {sortColumn === 'timestamp' && (
                        sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('event_type')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Event Type
                      {sortColumn === 'event_type' && (
                        sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('device')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Device / User
                      {sortColumn === 'device' && (
                        sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('client')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Client
                      {sortColumn === 'client' && (
                        sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('card_id')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Card ID
                      {sortColumn === 'card_id' && (
                        sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('status')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Status
                      {sortColumn === 'status' && (
                        sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedEvents.map((event) => (
                  <tr key={event.event_id}>
                    <td>{formatTimestamp(event.timestamp)}</td>
                    <td>
                      <span className={`badge ${getEventTypeBadge(event.event_type)}`}>
                        {formatEventType(event.event_type || 'card_scan')}
                      </span>
                    </td>
                    <td>{event.device_name || event.user_email || event.device_id || '-'}</td>
                    <td>{event.client_name || (event.client_id ? `Client #${event.client_id}` : '-')}</td>
                    <td>{event.card_id || '-'}</td>
                    <td>
                      <span className={`badge ${
                        event.status === 'granted' || event.status === 'started' || event.status === 'success' || event.status === 'verified' ? 'badge-success' :
                        event.status === 'denied' || event.status === 'failed' ? 'badge-danger' :
                        event.status === 'stopped' ? 'badge-info' :
                        'badge-warning'
                      }`}>
                        {event.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default EventsPage
